/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Zap, Building2, Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'For individual developers exploring fluxRun.',
    persuasion: null,
    features: [
      '25K executions',
      '7-day retention',
      'NO overage',
      'Unlimited team members',
      'Unlimited projects',
    ],
    cta: 'Current plan',
    highlighted: false,
    buttonVariant: 'outline',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$19',
    period: '/ month',
    description: 'For professional developers who need more power.',
    persuasion: 'Handle real production traffic without limits.',
    features: [
      '500K executions',
      '30-day retention',
      'Overage: $5 per additional 100K executions (Billed monthly)',
      'Unlimited team members',
      'Unlimited projects',
    ],
    cta: 'Upgrade to Pro',
    highlighted: true,
    buttonVariant: 'default',
  },
  {
    id: 'team',
    name: 'Team',
    price: '$49',
    period: '/ month',
    description: 'For engineering teams shipping together.',
    persuasion: 'Debug issues weeks after they happen.',
    features: [
      '2 million executions',
      '90-day retention',
      'Overage: $10 per additional 500K executions (Billed monthly)',
      'Unlimited team members',
      'Unlimited projects',
    ],
    cta: 'Upgrade to Team',
    highlighted: false,
    teamHighlight: true,
    buttonVariant: 'secondary',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: 'pricing',
    description: 'For large organizations with custom needs.',
    persuasion: null,
    features: [
      'Custom limits',
      'Custom retention',
      'Dedicated infrastructure',
      'Unlimited team members',
      'Unlimited projects',
    ],
    cta: 'Contact Us',
    highlighted: false,
    buttonVariant: 'ghost',
  },
];

interface SubscriptionManagerProps {
  userId: string;
  email: string;
}

function SubscriptionManagerContent({
  userId,
  email,
}: SubscriptionManagerProps): import('react/jsx-runtime').JSX.Element {
  const [loading, setLoading] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [usage, setUsage] = useState({ used: 0, limit: 25000 });
  const [, setUsageFetched] = useState(false);
  const [mounted, setMounted] = useState(false);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect((): void => setMounted(true), []);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect((): void => {
    async function fetchPlan(): Promise<void> {
      const syncSubId = searchParams.get('subscription_id');
      const qs = syncSubId != null ? `?sync_sub_id=${syncSubId}` : '';
      try {
        const res = await fetch(`/api/billing/subscription${qs}`);
        if (res.ok) {
          const data = await res.json();
          setCurrentPlan(data.plan ?? 'free');

          if (searchParams.has('success')) {
            navigate('/subscription', { replace: true });
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    void fetchPlan();
  }, [searchParams, navigate]);

  useEffect((): void => {
    async function fetchUsage(): Promise<void> {
      try {
        const res = await fetch('/api/orgs/active/usage');
        if (res.ok) {
          const data = await res.json();
          if (typeof data.used === 'number') {
            setUsage(data);
            setUsageFetched(true);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    void fetchUsage();
  }, []);

  const PLAN_RANKS: Record<string, number> = {
    free: 0,
    pro: 1,
    team: 2,
    enterprise: 3,
  };

  const PLAN_LIMITS: Record<string, number | null> = {
    free: 25000,
    pro: 500000,
    team: 2000000,
    enterprise: null,
  };

  const activeLimit = PLAN_LIMITS[currentPlan] ?? 25000;
  const isCustom = activeLimit === null;
  const usagePercentage = isCustom
    ? 0
    : Math.min(100, Math.max(0, (usage.used / activeLimit) * 100));
  const remaining = isCustom ? 0 : Math.max(0, activeLimit - usage.used);

  async function checkout(planId: string): Promise<void> {
    if (planId === currentPlan) return;
    setLoading(planId);
    try {
      if (planId === 'free') {
        const res = await fetch('/api/billing/portal', { method: 'POST' });
        const data = await res.json();
        if (Boolean(data.url)) window.location.href = data.url;
        return;
      }

      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId, userId, email }),
      });
      const data = await res.json();
      if (Boolean(data.checkoutUrl)) {
        window.location.href = data.checkoutUrl;
      } else if (Boolean(data.upgraded)) {
        window.location.href =
          '/subscription?success=1' +
          (Boolean(data.sync_sub_id) ? '&subscription_id=' + data.sync_sub_id : '');
      }
    } finally {
      setLoading(null);
    }
  }

  if (!mounted)
    return <div className="space-y-6 flex flex-col relative w-full min-h-[400px] animate-pulse" />;

  return (
    <div className="space-y-6 flex flex-col relative w-full h-full min-h-[400px]">
      {(loading != null || searchParams.has('success')) &&
      loading !== 'pro' &&
      loading !== 'team' ? (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl">
          <div className="flex flex-col items-center gap-4 text-muted-foreground p-6 bg-background border shadow-xl rounded-xl">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--flux-accent)]" />
            <p className="font-medium text-sm text-foreground">Syncing subscription...</p>
          </div>
        </div>
      ) : null}
      {/* Current plan banner */}
      <Card className="border-[var(--flux-accent)]/20 bg-[var(--flux-accent)]/5">
        <CardContent className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-6 w-full">
          <div className="flex w-full items-start gap-4 flex-1">
            <Zap className="h-5 w-5 text-[var(--flux-accent)] shrink-0 mt-0.5" />
            <div className="space-y-4 w-full">
              <p className="text-sm font-medium">
                You&apos;re on the{' '}
                <span className="capitalize text-[var(--flux-accent)]">{currentPlan}</span> plan
              </p>
              <div className="w-full max-w-xl">
                {isCustom ? (
                  <div className="flex justify-between items-end text-xs text-muted-foreground mb-2 w-full">
                    <span className="font-medium text-foreground">
                      {usage.used.toLocaleString()} executions used this month
                    </span>
                    <span>Custom limits apply</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-end text-xs text-muted-foreground mb-2 w-full">
                      <span className="font-medium text-foreground">
                        {usage.used.toLocaleString()} / {activeLimit.toLocaleString()} executions
                        used this month
                      </span>
                      <span>
                        {currentPlan === 'free' ? (
                          <>{remaining.toLocaleString()} remaining</>
                        ) : (
                          <>
                            {remaining > 0
                              ? `${remaining.toLocaleString()} remaining until overage`
                              : 'Overage applies'}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ease-in-out ${usagePercentage >= 100 ? 'bg-destructive' : 'bg-[var(--flux-accent)]'}`}
                        style={{ width: `${usagePercentage}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {currentPlan === 'free' && (
            <div className="flex flex-col items-start md:items-end justify-center shrink-0 min-w-[200px]">
              <p className="text-xs font-medium text-[var(--flux-accent)] mb-3 text-center md:text-right">
                Upgrade to Pro to avoid interruptions
              </p>
              <Button
                onClick={(): Promise<void> => checkout('pro')}
                size="sm"
                className="w-full md:w-auto"
              >
                Upgrade to Pro
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
        {PLANS.map((plan): import('react/jsx-runtime').JSX.Element => {
          const isCurrent = plan.id === currentPlan;

          let cardClasses = 'flex flex-col ';
          if (plan.highlighted) {
            cardClasses +=
              'border-[var(--flux-accent)]/40 ring-1 ring-[var(--flux-accent)]/20 relative mt-4 md:mt-0 overflow-visible';
          } else if (plan.teamHighlight ?? false) {
            cardClasses +=
              'border-[var(--flux-foreground)]/20 shadow-sm relative overflow-visible mt-4 md:mt-0';
          }

          return (
            <Card key={plan.id} className={cardClasses}>
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[var(--flux-accent)] text-white border-0 text-[10px] px-2 h-6 flex items-center justify-center whitespace-nowrap">
                    Popular
                  </Badge>
                </div>
              )}
              {(plan.teamHighlight ?? false) && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge
                    variant="secondary"
                    className="border-border text-[10px] px-2 h-6 flex items-center justify-center whitespace-nowrap"
                  >
                    Best for scaling teams
                  </Badge>
                </div>
              )}

              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{plan.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
                  </div>
                  {plan.id === 'team' && (
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                </div>
                <div className="mt-3">
                  <span className="text-2xl font-bold">{plan.price}</span>
                  <span className="text-xs text-muted-foreground ml-1">{plan.period}</span>
                </div>
                {plan.persuasion != null && (
                  <p className="text-[11px] font-medium text-[var(--flux-accent)] mt-2">
                    {plan.persuasion}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4 flex-1 flex flex-col pt-0 mt-2">
                <ul className="space-y-2 flex-1">
                  {plan.features.map((feature): import('react/jsx-runtime').JSX.Element => (
                    <li key={feature} className="flex items-start gap-2 text-xs">
                      <Check className="h-3.5 w-3.5 text-[var(--flux-success)] shrink-0 mt-px" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plan.buttonVariant as 'default' | 'secondary' | 'outline' | 'ghost'}
                  size="sm"
                  disabled={isCurrent || loading === plan.id}
                  onClick={(): Promise<void> | 'mailto:support@fluxrun.com' =>
                    plan.id === 'enterprise'
                      ? (window.location.href = 'mailto:support@fluxrun.com')
                      : checkout(plan.id)
                  }
                >
                  {loading === plan.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isCurrent
                    ? 'Current plan'
                    : (PLAN_RANKS[currentPlan] ?? 0) > (PLAN_RANKS[plan.id] ?? 0)
                      ? 'Downgrade'
                      : plan.cta}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Billing portal link */}
      {currentPlan !== 'free' && (
        <Card className="border-[var(--flux-accent)]/20 mt-6">
          <CardContent className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6">
            <div>
              <p className="text-base font-semibold">Manage billing & cancellation</p>
              <p className="text-sm text-muted-foreground mt-1">
                View invoices, update payment method, or cancel your subscription.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={async (): Promise<void> => {
                  const res = await fetch('/api/billing/portal', { method: 'POST' });
                  const data = await res.json();
                  if (Boolean(data.url)) window.location.href = data.url;
                }}
              >
                Manage billing
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={async (): Promise<void> => {
                  const res = await fetch('/api/billing/portal', { method: 'POST' });
                  const data = await res.json();
                  if (Boolean(data.url)) window.location.href = data.url;
                }}
              >
                Cancel subscription
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col items-center justify-center space-y-1 mt-8">
        <p className="text-sm font-medium text-muted-foreground">Cancel anytime.</p>
        <p className="text-[11px] text-muted-foreground text-center">
          Payments powered by{' '}
          <a
            href="https://dodopayments.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground transition-colors"
          >
            Dodo Payments
          </a>
        </p>
      </div>
    </div>
  );
}

export function SubscriptionManager(
  props: SubscriptionManagerProps,
): import('react/jsx-runtime').JSX.Element {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center p-12 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      }
    >
      <SubscriptionManagerContent {...props} />
    </Suspense>
  );
}
